import {
  GraduationCap, LogIn, MailPlus, MicOff, MoreHorizontal, Plus, Search, Settings2, Trash2, UserCheck,
  UserCog, UserX, Users as UsersIcon,
} from "lucide-react";
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Avatar, CopyButton, Tabs, TabsContent, TabsList, TabsTrigger, Tooltip } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { InviteUserDialog } from "@/features/invite-form";
import { InvitationSentDialog, InvitationsPanel, InvitationsSentDialog } from "@/features/invitations";
import { SignInLinkDialog } from "@/features/sign-in-link";
import { UserFormDialog } from "@/features/user-form";
import { UserSkillsDialog } from "@/features/user-skills";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  useDeleteUser, useDomains, useInvitations, useMakeSignInLink, useUpdateUser, useUsers,
} from "@/lib/queries";
import {
  ROLES, ROLE_LABELS, assignableRoles, atLeast, type AppUser, type InvitationBulkSent,
  type InvitationSent,
} from "@/lib/types";
import { relativeTime } from "@/lib/utils";
import { useDebounced } from "@/lib/use-debounced";

export function UsersPage() {
  const { user: actor } = useAuth();
  const toast = useToast();
  const canManage = atLeast(actor?.role, "admin");

  // The tab is in the URL (`?tab=invitations`), so a reload lands where the admin was. Invitations
  // are an admin's (ADR-028 in `platform`), so a supervisor gets the one list and no tabs.
  const [params, setParams] = useSearchParams();
  const tab = canManage && params.get("tab") === "invitations" ? "invitations" : "users";

  const [search, setSearch] = React.useState("");
  const [role, setRole] = React.useState<string>("all");
  const [domainId, setDomainId] = React.useState<string>("all");
  const debouncedSearch = useDebounced(search, 250);

  const { data: domains } = useDomains();
  const { data, isPending, error, refetch } = useUsers({
    q: debouncedSearch || undefined,
    role: role === "all" ? undefined : role,
    domain_id: domainId === "all" ? undefined : Number(domainId),
  });

  const [editing, setEditing] = React.useState<AppUser | undefined>();
  const [formOpen, setFormOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState<AppUser | undefined>();
  const [deactivating, setDeactivating] = React.useState<AppUser | undefined>();
  const remove = useDeleteUser();
  const update = useUpdateUser();
  // The person stays set after the dialog closes, so it does not empty while it animates shut.
  const [skillsOf, setSkillsOf] = React.useState<AppUser | undefined>();
  const [skillsOpen, setSkillsOpen] = React.useState(false);
  // Sign in as (ADR-040): who, kept after the dialog closes like `skillsOf`, and the link made.
  const [signInAs, setSignInAs] = React.useState<AppUser | undefined>();
  const [signInOpen, setSignInOpen] = React.useState(false);
  const makeSignInLink = useMakeSignInLink();
  const openSignInAs = (person: AppUser) => {
    setSignInAs(person);
    setSignInOpen(true);
    makeSignInLink.mutate(person.id);
  };

  const [inviting, setInviting] = React.useState(false);
  // Kept after the dialog closes, like `skillsOf`.
  const [sent, setSent] = React.useState<InvitationSent | undefined>();
  const [sentOpen, setSentOpen] = React.useState(false);
  const [sentMany, setSentMany] = React.useState<InvitationBulkSent | undefined>();
  const [sentManyOpen, setSentManyOpen] = React.useState(false);
  const invitations = useInvitations({ enabled: canManage, refetchInterval: 60_000 });
  const pending = (invitations.data ?? []).filter((i) => i.status === "pending").length;

  /** The link of an invitation just sent is shown once, over the list it now sits in. */
  const showSent = (result: InvitationSent) => {
    setSent(result);
    setSentOpen(true);
    setParams({ tab: "invitations" }, { replace: true });
  };
  const showSentMany = (result: InvitationBulkSent) => {
    setSentMany(result);
    setSentManyOpen(true);
    setParams({ tab: "invitations" }, { replace: true });
  };

  const filtered = Boolean(debouncedSearch) || role !== "all" || domainId !== "all";

  const usersCard = (
    <Card className="overflow-hidden">
      <Toolbar>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search email or name"
          leading={<Search />}
          className="w-56"
          aria-label="Search users"
        />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-36" aria-label="Filter by role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ROLES.map((r) => (
              <SelectItem key={r} value={r}>
                {ROLE_LABELS[r]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={domainId} onValueChange={setDomainId}>
          <SelectTrigger className="w-48" aria-label="Filter by domain">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All domains</SelectItem>
            {(domains ?? []).map((d) => (
              <SelectItem key={d.id} value={String(d.id)}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filtered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch("");
              setRole("all");
              setDomainId("all");
            }}
          >
            Clear
          </Button>
        )}
        <span className="ml-auto text-xs text-text-muted">
          {data ? `${data.length} user${data.length === 1 ? "" : "s"}` : null}
        </span>
      </Toolbar>

      {isPending ? (
        <TableSkeleton cols={6} />
      ) : error ? (
        <ErrorState
          error={error}
          title="Could not load users"
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      ) : data.length === 0 ? (
        <EmptyState
          icon={UsersIcon}
          title={filtered ? "No users match those filters" : "No users yet"}
          description={
            filtered
              ? "Try widening the search, or clearing the role and domain filters."
              : "Create the first account to get somebody signed in."
          }
          action={
            filtered ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch("");
                  setRole("all");
                  setDomainId("all");
                }}
              >
                Clear filters
              </Button>
            ) : canManage ? (
              <Button variant="default" onClick={() => setFormOpen(true)}>
                <Plus />
                New user
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Domain</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Last seen</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((u) => (
              // 44px rows, not the table's usual 40: the first cell stacks a name over an
              // email, and at 40 those two lines all but touch the rows above and below.
              <TableRow key={u.id} className="[&>td]:h-11">
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <Avatar name={u.display_name} email={u.email} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-medium text-text">
                          {u.display_name || u.email}
                        </span>
                        {!u.is_active && (
                          <Badge tone="danger" size="sm">
                            Disabled
                          </Badge>
                        )}
                        {u.id === actor?.id && (
                          <Badge tone="outline" size="sm">
                            You
                          </Badge>
                        )}
                      </div>
                      {u.display_name && (
                        <span className="block truncate text-xs text-text-muted">{u.email}</span>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge tone={u.role === "superadmin" ? "accent" : "neutral"}>
                    {ROLE_LABELS[u.role]}
                  </Badge>
                </TableCell>
                <MonoCell className="text-text-muted">
                  {u.domain_name ?? <span className="font-sans text-text-subtle">All</span>}
                </MonoCell>
                <TableCell>
                  {u.sip_endpoint_id ? (
                    <span className="inline-flex items-center gap-1.5">
                      <StatusDot state={null} label="Has a phone" />
                      <span className="font-mono text-xs">{u.sip_endpoint_extension}</span>
                      <CopyButton value={u.sip_endpoint_extension ?? ""} />
                      {/* Recording is on for everyone by default, so only the exception is
                          worth a mark — a column of identical icons says nothing. */}
                      {u.sip_endpoint_record_calls === false && (
                        <Tooltip content="Recording is off for this phone">
                          <MicOff className="size-3.5 text-text-subtle" aria-label="Not recorded" />
                        </Tooltip>
                      )}
                      {u.sip_endpoint_enabled === false && (
                        <Badge tone="warning" size="sm">
                          Disabled
                        </Badge>
                      )}
                    </span>
                  ) : (
                    <span className="text-xs text-text-subtle">None</span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-text-muted">
                  {relativeTime(u.last_login_at)}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${u.email}`}>
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {/* On every row, the reader's own included: a supervisor reads a rep's
                          skills, and an admin sets their own, since admins take calls too.
                          Saving is the admin's alone, in the dialog. */}
                      <DropdownMenuItem
                        onSelect={() => {
                          setSkillsOf(u);
                          setSkillsOpen(true);
                        }}
                      >
                        <GraduationCap />
                        Skills
                      </DropdownMenuItem>
                      {canManage && u.id !== actor?.id && (
                        <>
                          <DropdownMenuItem
                            onSelect={() => {
                              setEditing(u);
                              setFormOpen(true);
                            }}
                          >
                            <UserCog />
                            Edit user
                          </DropdownMenuItem>
                          {/* Whoever an admin may manage, as `rbac.can_manage_user` has it: the
                              server refuses the rest, and a disabled account gets no link. */}
                          {u.is_active && assignableRoles(actor?.role).includes(u.role) && (
                            <DropdownMenuItem onSelect={() => openSignInAs(u)}>
                              <LogIn />
                              Sign in as
                            </DropdownMenuItem>
                          )}
                          {u.is_active ? (
                            <DropdownMenuItem onSelect={() => setDeactivating(u)}>
                              <UserX />
                              Deactivate
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onSelect={() => void setActive(u, true)}>
                              <UserCheck />
                              Reactivate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem destructive onSelect={() => setDeleting(u)}>
                            <Trash2 />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                      {u.id === actor?.id && (
                        <DropdownMenuItem asChild>
                          <Link to="/settings/account">
                            <Settings2 />
                            Edit your account in Settings
                          </Link>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );

  /** Turn an account off or on. Deactivating is confirmed first — it signs them out mid-call
   *  if they are on one — while reactivating only gives access back. */
  async function setActive(user: AppUser, active: boolean) {
    try {
      await update.mutateAsync({ id: user.id, is_active: active });
      toast.success(active ? "User reactivated" : "User deactivated", user.email);
      setDeactivating(undefined);
    } catch (e) {
      toast.error(
        active ? "Could not reactivate" : "Could not deactivate",
        e instanceof ApiError ? e.message : "Please try again.",
      );
    }
  }

  return (
    <Page>
      <PageHeader
        title="Users"
        description="Who can sign in, what they can reach, and the phone that came with them."
        action={
          canManage && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setEditing(undefined);
                  setFormOpen(true);
                }}
              >
                <Plus />
                New user
              </Button>
              <Button variant="default" onClick={() => setInviting(true)}>
                <MailPlus />
                Invite user
              </Button>
            </div>
          )
        }
      />

      {canManage ? (
        <Tabs
          value={tab}
          onValueChange={(next) => setParams(next === "users" ? {} : { tab: next }, { replace: true })}
        >
          <TabsList>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="invitations">
              <span className="inline-flex items-center gap-1.5">
                Invitations
                {pending > 0 && (
                  <Badge tone="accent" size="sm" aria-label={`${pending} pending`}>
                    {pending}
                  </Badge>
                )}
              </span>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="pt-4">
            {usersCard}
          </TabsContent>
          <TabsContent value="invitations" className="pt-4">
            <InvitationsPanel onInvite={() => setInviting(true)} onSent={showSent} />
          </TabsContent>
        </Tabs>
      ) : (
        usersCard
      )}

      <UserFormDialog open={formOpen} onOpenChange={setFormOpen} user={editing} />
      {canManage && (
        <>
          <InviteUserDialog
            open={inviting}
            onOpenChange={setInviting}
            onSent={showSent}
            onSentMany={showSentMany}
          />
          <InvitationSentDialog sent={sent} open={sentOpen} onOpenChange={setSentOpen} />
          <InvitationsSentDialog sent={sentMany} open={sentManyOpen} onOpenChange={setSentManyOpen} />
          <SignInLinkDialog
            user={signInAs}
            made={makeSignInLink.data}
            error={makeSignInLink.error}
            pending={makeSignInLink.isPending}
            open={signInOpen}
            onOpenChange={setSignInOpen}
            onRetry={() => signInAs && makeSignInLink.mutate(signInAs.id)}
          />
        </>
      )}
      <UserSkillsDialog
        open={skillsOpen}
        onOpenChange={setSkillsOpen}
        user={skillsOf}
        canEdit={canManage}
        isSelf={skillsOf !== undefined && skillsOf.id === actor?.id}
      />
      <ConfirmDialog
        open={Boolean(deactivating)}
        onOpenChange={(open) => !open && setDeactivating(undefined)}
        title="Deactivate this user?"
        description={
          <>
            <span className="font-medium">{deactivating?.email}</span> is signed out everywhere
            straight away and cannot sign in again, so do this knowing they may be on a call.
            Their account, their phone and their history all stay as they are, and reactivating
            gives everything back.
          </>
        }
        confirmLabel="Deactivate"
        loading={update.isPending}
        onConfirm={() => deactivating && setActive(deactivating, false)}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        title="Delete this user?"
        description={
          <>
            <span className="font-medium">{deleting?.email}</span> will be removed and signed out
            everywhere, and{" "}
            {deleting?.sip_endpoint_extension ? (
              <>
                extension{" "}
                <span className="font-mono">{deleting.sip_endpoint_extension}</span> goes with
                them, back into the pool
              </>
            ) : (
              <>their phone, if they have one, goes with them</>
            )}
            . Their calls stay in the history. This cannot be undone.
          </>
        }
        confirmLabel="Delete user"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            toast.success("User deleted", deleting.email);
            setDeleting(undefined);
          } catch (e) {
            toast.error(
              "Could not delete",
              e instanceof ApiError ? e.message : "Please try again.",
            );
          }
        }}
      />
    </Page>
  );
}
